import Api from "api/Api";
import type { AxiosResponse, CancelTokenSource } from "axios";
import { omit } from "lodash";

interface ResponseMeta {
  status: number;
  success: boolean;
  error?: any;
}

interface ApiResponse<T = any> {
  responseMeta: ResponseMeta;
  data: T;
  errorDisplay?: string;
  code?: string;
}

export type AxiosPromise<T = any> = Promise<ApiResponse<T>>;

export type ApiAxiosPromise<T = any> = Promise<AxiosResponse<T>>;

export interface ProcessDesignRequest {
  applicationId?: string;
  name?: string;
  key?: string;
  enabled?: boolean;
  createdAtStart?: string;
  createdAtEnd?: string;
}

export interface ProcessDesignDetailsRequest {
  processId: string;
}

export interface CreateProcessDesignRequest {
  name?: string;
  key?: string;
  enabled?: boolean;
  publish?: boolean;
  processId?: string;
  graph?: any;
}

/** 流程申请状态 */
export type ExecutionsStatus = "running" | "succeeded" | "failed" | "canceled";

/**
 * 审批流接口
 */
class ProcessDesignApi extends Api {
  static url = "v1/processes";
  static exeUrl = "v1/executions";
  static taskUrl = "v1/tasks";
  static taskActivityUrl = "v1/task-activities";
  static taskDashboardUrl = "v1/task-dashboard";
  static executionDashboardUrl = "v1/execution-dashboard";
  static pageUpdateCancelTokenSource?: CancelTokenSource = undefined;

  /** -------------------------------------------------------------- 流程设计相关接口 -------------------------------------------------------------- */

  /**
   * 获取流程设计列表
   * @param request
   * @returns
   */
  static async getProcessDesign(
    request: ProcessDesignRequest,
  ): ApiAxiosPromise<any> {
    return Api.get(`${ProcessDesignApi.url}`, request);
  }

  /**
   * 获取流程设计详情
   * @param request
   * @returns
   */
  static async getProcessDesignDetails(
    request: ProcessDesignDetailsRequest,
  ): ApiAxiosPromise<any> {
    return Api.get(`${ProcessDesignApi.url}/${request.processId}`);
  }

  /**
   * 创建流程设计
   * @param request
   * @returns
   */
  static async createProcessDesign(
    request: CreateProcessDesignRequest,
  ): ApiAxiosPromise<any> {
    return Api.post(`${ProcessDesignApi.url}`, request);
  }

  /**
   * 更新流程设计
   * @param request
   * @returns
   */
  static async updateProcessDesign(
    request: CreateProcessDesignRequest,
  ): ApiAxiosPromise<any> {
    return Api.put(
      `${ProcessDesignApi.url}/${request.processId}`,
      omit(request, ["processId"]),
    );
  }

  /**
   * 更新流程设计图
   * @param request
   * @returns
   */
  static async updateProcessDesignGraph(
    request: CreateProcessDesignRequest,
  ): ApiAxiosPromise<any> {
    return Api.put(
      `${ProcessDesignApi.url}/${request.processId}/graph`,
      omit(request, ["processId"]),
    );
  }

  /**
   * 删除流程设计
   * @param processId
   * @returns
   */
  static async deleteProcessDesign(
    /** 流程id */
    processId: string,
  ): ApiAxiosPromise<any> {
    return Api.delete(`${ProcessDesignApi.url}/${processId}`);
  }

  /** -------------------------------------------------------------- 流程申请相关接口 -------------------------------------------------------------- */

  /**
   * 查询流程申请列表
   * @param status
   * @returns
   */
  static async getProcessApplicationList(request: {
    status?: ExecutionsStatus;
  }): ApiAxiosPromise<any[]> {
    return Api.get(`${ProcessDesignApi.exeUrl}`, request);
  }

  /**
   * 查询流程申请详情
   */
  static async getProcessApplicationDetails(
    /** 执行id */
    executionId: string,
  ): ApiAxiosPromise<any> {
    return Api.get(`${ProcessDesignApi.exeUrl}/${executionId}`);
  }

  /**
   * 发起流程申请
   */
  static async startProcessApplication(request: {
    /** 流程id */
    processId?: string;
    title?: string;
    formData?: Record<string, any>;
  }): ApiAxiosPromise<any> {
    return Api.post(`${ProcessDesignApi.exeUrl}`, request);
  }

  /**
   * 取消流程申请
   */
  static async cancelProcessApplication(
    /** 执行id */
    executionId: string,
  ): ApiAxiosPromise<any> {
    return Api.post(`${ProcessDesignApi.exeUrl}/${executionId}/terminate`);
  }

  /**
   * 查询某个执行记录下的所有表单数据
   */
  static async getProcessApplicationFormData(
    /** 执行id */
    executionId: string,
  ): ApiAxiosPromise<any> {
    return Api.get(`${ProcessDesignApi.exeUrl}/${executionId}/forms`);
  }

  /**
   * 查询当前用户相关的执行记录的控制台统计数据
   */
  static async getExecutionDashboard(): ApiAxiosPromise<any> {
    return Api.get(`${ProcessDesignApi.executionDashboardUrl}`);
  }

  /** -------------------------------------------------------------- 任务相关接口 -------------------------------------------------------------- */

  /**
   * 查询任务列表
   */
  static async getTaskList(request: {
    /** 筛选条件 myPending: 我的待办 myCompleted: 我的已办 myInvolved: 我参与的 */
    filter?: "myPending" | "myCompleted" | "myInvolved" | "myForm" | "form";
    /** 查询某次执行流程相关的任务。 */
    executionId?: string;
    /** 分页参数 */
    size?: number;
    page?: number;
  }): ApiAxiosPromise<any> {
    return Api.get(`${ProcessDesignApi.taskUrl}`, request);
  }

  /**
   * 查询任务详情
   */
  static async getTaskDetails(
    /** 任务id */
    taskId: string,
  ): ApiAxiosPromise<any> {
    return Api.get(`${ProcessDesignApi.taskUrl}/${taskId}`);
  }

  /**
   * 审批通过
   */
  static async approveTask(
    /** 任务id */
    taskId: string,
    request: { comment?: string },
  ): ApiAxiosPromise<any> {
    return Api.post(`${ProcessDesignApi.taskUrl}/${taskId}/approve`, request);
  }

  /**
   * 审批不通过
   */
  static async rejectTask(
    /** 任务id */
    taskId: string,
    request: { comment?: string },
  ): ApiAxiosPromise<any> {
    return Api.post(`${ProcessDesignApi.taskUrl}/${taskId}/reject`, request);
  }

  /**
   * 转交任务
   * 执行审批动作，例如通过、拒绝等操作。
   */
  static async transferTask(
    /** 任务id */
    taskId: string,
    request: { assigneeUserId: string; comment?: string },
  ): ApiAxiosPromise<any> {
    return Api.post(`${ProcessDesignApi.taskUrl}/${taskId}/transfer`, request);
  }

  /**
   * 发表评论
   */
  static async commentTask(
    /** 任务id */
    taskId: string,
    request: { comment: string },
  ): ApiAxiosPromise<any> {
    return Api.post(`${ProcessDesignApi.taskUrl}/${taskId}/comment`, request);
  }

  /**
   * 查询任务操作记录
   */
  static async getTaskActivities(request: {
    /** 任务id 查询某个任务的操作记录 */
    taskId?: string;
    /** 执行id 查询某次执行流程的所有任务操作记录 */
    executionId?: string;
  }): ApiAxiosPromise<any> {
    return Api.get(`${ProcessDesignApi.taskActivityUrl}`, request);
  }

  /**
   * 提交表单节点
   */
  static async submitFormTask(
    taskId: string,
    request: any,
  ): ApiAxiosPromise<any> {
    return Api.post(
      `${ProcessDesignApi.taskUrl}/${taskId}/form-submit`,
      request,
    );
  }

  /**
   * 查询当前用户相关的任务的控制台统计数据
   */
  static async getTaskDashboard(): ApiAxiosPromise<any> {
    return Api.get(`${ProcessDesignApi.taskDashboardUrl}`);
  }

  /** -------------------------------------------------------------- 应用相关接口 -------------------------------------------------------------- */

  /**
   * 查询应用页面列表
   */
  static async getApplicationList(applicationId: string): ApiAxiosPromise<any> {
    return Api.get(`/v1/pages?applicationId=${applicationId}`);
  }

  /**
   * 查询页面详情
   */
  static async getPageDetails(pageId: string): ApiAxiosPromise<any> {
    return Api.get(`/v1/pages/${pageId}`);
  }
}

export default ProcessDesignApi;
